/**
 * Iter-235 — Event Schedule Editor.
 *
 * One screen, two superpowers:
 *   1. Edit every day of a multi-day event inline (date / time / role /
 *      qty / pay / bill / notes).
 *   2. Add staggered shift blocks within a single day (e.g. BA 9-5 AM
 *      wave + BA 11-7 PM wave on the same day).
 *
 * Calls the existing /api/admin/multi-slot-events backend:
 *   - GET    /by-shift/{shift_id}             -> list slots
 *   - POST   /by-shift/{shift_id}/promote     -> solo shift -> series
 *   - POST   /{series_id}/slots               -> add a block
 *   - PATCH  /{series_id}/slots/{shift_id}    -> save edits
 *   - DELETE /{series_id}/slots/{shift_id}    -> remove a block
 *
 * Used by EventDetailDrawer's "Schedule" tab.
 *
 * Does NOT change the visual identity of the new dashboard — it sits
 * inside the existing drawer and uses the same color tokens.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Plus, Copy, Trash2, Save, Calendar, Loader2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { formatDate } from '../utils/timeFormat';
import api from '../utils/api';
import { toast } from 'sonner';

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

function emptySlot(seed = {}) {
  return {
    shift_id: null,
    _local_id: `new-${Math.random().toString(36).slice(2, 9)}`,
    role_id: seed.role_id || '',
    role_name: seed.role_name || '',
    shift_date: seed.shift_date || new Date().toISOString().slice(0, 10),
    start_time: seed.start_time || '09:00',
    end_time: seed.end_time || '17:00',
    talent_needed: seed.talent_needed || 1,
    bill_rate: seed.bill_rate || 0,
    talent_pay_rate: seed.talent_pay_rate || null,
    duties: seed.duties || '',
    dress_code_name: seed.dress_code_name || null,
    important_notes: seed.important_notes || '',
    additional_notes: seed.additional_notes || '',
    filled_count: 0,
    applicant_count: 0,
    status: 'open',
    _dirty: true,
    _new: true,
  };
}

export default function EventScheduleEditor({ shiftId, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [series, setSeries] = useState(null);
  const [slots, setSlots] = useState([]);
  const [roles, setRoles] = useState([]);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [seriesRes, rolesRes] = await Promise.all([
        api.get(`/admin/multi-slot-events/by-shift/${shiftId}`),
        api.get('/roles').catch(() => ({ data: [] })),
      ]);
      setSeries(seriesRes.data);
      setSlots((seriesRes.data.slots || []).map((s) => ({ ...s, _dirty: false, _new: false })));
      setRoles(rolesRes.data || []);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, [shiftId]);

  useEffect(() => { if (shiftId) load(); }, [shiftId, load]);

  // Group by date for the visual day-by-day preview
  const groupedByDay = useMemo(() => {
    const groups = {};
    for (const s of slots) {
      const day = s.shift_date || 'TBD';
      groups[day] = groups[day] || [];
      groups[day].push(s);
    }
    return Object.entries(groups)
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .map(([date, items]) => ({
        date,
        items: items.slice().sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')),
      }));
  }, [slots]);

  const patchSlot = (idx, patch) => {
    setSlots((prev) => {
      const next = prev.slice();
      next[idx] = { ...next[idx], ...patch, _dirty: true };
      return next;
    });
  };

  const ensureSeries = async () => {
    // If we're working on a synthesized (solo) shift, promote to series.
    if (!series?.series_id) {
      const res = await api.post(`/admin/multi-slot-events/by-shift/${shiftId}/promote`);
      const sid = res.data.series_id;
      setSeries((p) => ({ ...(p || {}), series_id: sid }));
      return sid;
    }
    return series.series_id;
  };

  const addBlock = (seed) => {
    setSlots((prev) => [...prev, emptySlot(seed)]);
  };

  const duplicateBlock = (idx) => {
    const src = slots[idx];
    addBlock({
      role_id: src.role_id,
      role_name: src.role_name,
      shift_date: src.shift_date,
      start_time: src.start_time,
      end_time: src.end_time,
      talent_needed: src.talent_needed,
      bill_rate: src.bill_rate,
      talent_pay_rate: src.talent_pay_rate,
      duties: src.duties,
      dress_code_name: src.dress_code_name,
    });
  };

  const copyDay = (date) => {
    const items = slots.filter((s) => s.shift_date === date);
    const next = new Date(date);
    next.setDate(next.getDate() + 1);
    const newDate = next.toISOString().slice(0, 10);
    items.forEach((src) => addBlock({
      role_id: src.role_id,
      role_name: src.role_name,
      shift_date: newDate,
      start_time: src.start_time,
      end_time: src.end_time,
      talent_needed: src.talent_needed,
      bill_rate: src.bill_rate,
      talent_pay_rate: src.talent_pay_rate,
      duties: src.duties,
      dress_code_name: src.dress_code_name,
    }));
  };

  const removeSlot = async (idx) => {
    const s = slots[idx];
    if (s._new) {
      setSlots((prev) => prev.filter((_, i) => i !== idx));
      return;
    }
    if (s.filled_count > 0) {
      toast.error('This block has booked talent. Reassign them before deleting.');
      return;
    }
    if (!window.confirm('Remove this shift block? Pending applicants will be detached.')) return;
    try {
      const sid = series.series_id;
      await api.delete(`/admin/multi-slot-events/${sid}/slots/${s.shift_id}`);
      setSlots((prev) => prev.filter((_, i) => i !== idx));
      toast.success('Block removed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove block');
    }
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const sid = await ensureSeries();
      let updated = 0;
      let created = 0;
      for (const s of slots) {
        if (!s._dirty) continue;
        const payload = {
          role_id: s.role_id || null,
          shift_date: s.shift_date,
          start_time: s.start_time,
          end_time: s.end_time,
          talent_needed: Number(s.talent_needed) || 1,
          bill_rate: Number(s.bill_rate) || 0,
          talent_pay_rate: s.talent_pay_rate ? Number(s.talent_pay_rate) : null,
          duties: s.duties || null,
          dress_code_name: s.dress_code_name || null,
          important_notes: s.important_notes || null,
          additional_notes: s.additional_notes || null,
        };
        if (s._new) {
          if (!payload.role_id) throw new Error('Each block needs a role.');
          await api.post(`/admin/multi-slot-events/${sid}/slots`, payload);
          created += 1;
        } else {
          await api.patch(`/admin/multi-slot-events/${sid}/slots/${s.shift_id}`, payload);
          updated += 1;
        }
      }
      toast.success(`Saved ${updated} updates, ${created} new blocks`);
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="schedule-editor-loading">
        <Loader2 className="w-5 h-5 animate-spin text-cyan-600" />
      </div>
    );
  }
  if (err) {
    return (
      <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-rose-800 text-xs flex items-start gap-2">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {err}
      </div>
    );
  }

  const dirtyCount = slots.filter((s) => s._dirty).length;

  return (
    <div className="space-y-3" data-testid="schedule-editor">
      {/* Day-by-day calendar preview */}
      <div className="space-y-3">
        {groupedByDay.map(({ date, items }) => (
          <div key={date} className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-slate-50 px-3 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                <Calendar className="w-3.5 h-3.5 text-cyan-600" />
                {formatDate(date) || date}
                <span className="text-[10px] font-normal text-slate-500">
                  · {items.length} block{items.length === 1 ? '' : 's'} · {items.reduce((t, s) => t + Number(s.talent_needed || 0), 0)} talent
                </span>
              </div>
              <Button
                size="sm" variant="outline" className="h-7 text-[10px] gap-1"
                onClick={() => copyDay(date)}
                data-testid={`schedule-copy-day-${date}`}
              >
                <Copy className="w-3 h-3" /> Copy day
              </Button>
            </div>

            <ul className="divide-y divide-slate-100">
              {items.map((s) => {
                const idx = slots.indexOf(s);
                return (
                  <li key={s._local_id || s.shift_id} className={`p-3 ${s._dirty ? 'bg-amber-50/40' : ''}`} data-testid={`schedule-block-${s.shift_id || s._local_id}`}>
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-2">
                      <div className="col-span-2 md:col-span-2">
                        <Label className="text-[10px] uppercase text-slate-500">Role</Label>
                        <select
                          className="w-full text-xs border rounded p-1.5"
                          value={s.role_id || ''}
                          onChange={(e) => {
                            const r = roles.find((x) => String(x.id) === e.target.value);
                            patchSlot(idx, { role_id: e.target.value, role_name: r?.name || '' });
                          }}
                          data-testid={`schedule-role-${s.shift_id || s._local_id}`}
                        >
                          <option value="">— Pick role —</option>
                          {roles.map((r) => (
                            <option key={r.id} value={r.id}>{r.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase text-slate-500">Qty</Label>
                        <Input type="number" min={1} className="h-8 text-xs"
                               value={s.talent_needed}
                               onChange={(e) => patchSlot(idx, { talent_needed: e.target.value })}
                               data-testid={`schedule-qty-${s.shift_id || s._local_id}`} />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase text-slate-500">Date</Label>
                        <Input type="date" className="h-8 text-xs"
                               value={s.shift_date || ''}
                               onChange={(e) => patchSlot(idx, { shift_date: e.target.value })}
                               data-testid={`schedule-date-${s.shift_id || s._local_id}`} />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase text-slate-500">Start</Label>
                        <Input type="time" className="h-8 text-xs"
                               value={s.start_time || ''}
                               onChange={(e) => patchSlot(idx, { start_time: e.target.value })}
                               data-testid={`schedule-start-${s.shift_id || s._local_id}`} />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase text-slate-500">End</Label>
                        <Input type="time" className="h-8 text-xs"
                               value={s.end_time || ''}
                               onChange={(e) => patchSlot(idx, { end_time: e.target.value })}
                               data-testid={`schedule-end-${s.shift_id || s._local_id}`} />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                      <div>
                        <Label className="text-[10px] uppercase text-slate-500">Bill rate</Label>
                        <Input type="number" step="0.01" className="h-8 text-xs"
                               value={s.bill_rate}
                               onChange={(e) => patchSlot(idx, { bill_rate: e.target.value })}
                               data-testid={`schedule-bill-${s.shift_id || s._local_id}`} />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase text-slate-500">Pay rate</Label>
                        <Input type="number" step="0.01" className="h-8 text-xs" placeholder="auto 70%"
                               value={s.talent_pay_rate ?? ''}
                               onChange={(e) => patchSlot(idx, { talent_pay_rate: e.target.value || null })}
                               data-testid={`schedule-pay-${s.shift_id || s._local_id}`} />
                      </div>
                      <div className="col-span-2 md:col-span-2 flex items-end gap-1.5 text-[10px] text-slate-500">
                        {s.filled_count > 0 && <span className="px-1.5 py-0.5 rounded bg-cyan-50 border border-cyan-200 text-cyan-700">{s.filled_count} filled</span>}
                        {s.applicant_count > 0 && <span className="px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700">{s.applicant_count} applicants</span>}
                        <span className="ml-auto text-[10px] text-slate-400">{fmtMoney(s.bill_rate)} bill · pay {s.talent_pay_rate ? fmtMoney(s.talent_pay_rate) : 'auto'}</span>
                      </div>
                    </div>

                    <details className="text-[11px]">
                      <summary className="cursor-pointer text-slate-500 hover:text-slate-700">Block-level overrides (notes &amp; briefing)</summary>
                      <div className="mt-2 space-y-2 pl-2 border-l-2 border-slate-100">
                        <div>
                          <Label className="text-[10px] uppercase text-slate-500">Duties for this block</Label>
                          <Textarea rows={2} className="text-xs"
                                    value={s.duties || ''}
                                    onChange={(e) => patchSlot(idx, { duties: e.target.value })}
                                    data-testid={`schedule-duties-${s.shift_id || s._local_id}`} />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase text-slate-500">Important notes (override)</Label>
                          <Textarea rows={2} className="text-xs" placeholder="Leave blank to inherit from parent event"
                                    value={s.important_notes || ''}
                                    onChange={(e) => patchSlot(idx, { important_notes: e.target.value })}
                                    data-testid={`schedule-important-${s.shift_id || s._local_id}`} />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase text-slate-500">Additional notes (override)</Label>
                          <Textarea rows={2} className="text-xs" placeholder="Leave blank to inherit from parent event"
                                    value={s.additional_notes || ''}
                                    onChange={(e) => patchSlot(idx, { additional_notes: e.target.value })}
                                    data-testid={`schedule-additional-${s.shift_id || s._local_id}`} />
                        </div>
                      </div>
                    </details>

                    <div className="flex items-center gap-1.5 mt-2">
                      <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1"
                              onClick={() => duplicateBlock(idx)}
                              data-testid={`schedule-duplicate-${s.shift_id || s._local_id}`}>
                        <Copy className="w-3 h-3" /> Duplicate block
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-[10px] gap-1 text-rose-600 hover:bg-rose-50"
                              onClick={() => removeSlot(idx)}
                              data-testid={`schedule-remove-${s.shift_id || s._local_id}`}>
                        <Trash2 className="w-3 h-3" /> Remove
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>

            <div className="px-3 py-2 bg-slate-50/60 border-t border-slate-100">
              <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1 w-full"
                      onClick={() => addBlock({
                        shift_date: date,
                        start_time: items[items.length - 1]?.end_time || '09:00',
                        end_time: '17:00',
                        bill_rate: items[items.length - 1]?.bill_rate || 0,
                        role_id: items[items.length - 1]?.role_id || '',
                        role_name: items[items.length - 1]?.role_name || '',
                      })}
                      data-testid={`schedule-add-block-${date}`}>
                <Plus className="w-3 h-3" /> Add shift block to {formatDate(date) || date}
              </Button>
            </div>
          </div>
        ))}

        <Button variant="outline" className="w-full gap-2 border-dashed"
                onClick={() => {
                  const lastDay = groupedByDay[groupedByDay.length - 1];
                  const seed = lastDay?.items?.[0];
                  const next = lastDay ? new Date(lastDay.date) : new Date();
                  next.setDate(next.getDate() + 1);
                  addBlock({
                    shift_date: next.toISOString().slice(0, 10),
                    start_time: seed?.start_time || '09:00',
                    end_time: seed?.end_time || '17:00',
                    bill_rate: seed?.bill_rate || 0,
                    role_id: seed?.role_id || '',
                    role_name: seed?.role_name || '',
                  });
                }}
                data-testid="schedule-add-day">
          <Plus className="w-4 h-4" /> Add a day
        </Button>
      </div>

      {/* Sticky save bar */}
      <div className="sticky bottom-0 -mx-5 px-5 py-2 bg-white border-t border-slate-200 flex items-center justify-between">
        <span className="text-[11px] text-slate-500">
          {dirtyCount > 0 ? `${dirtyCount} unsaved change${dirtyCount === 1 ? '' : 's'}` : 'All changes saved'}
        </span>
        <div className="flex items-center gap-2">
          {onClose && <Button size="sm" variant="ghost" onClick={onClose} data-testid="schedule-close">Done</Button>}
          <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white gap-1.5"
                  onClick={saveAll}
                  disabled={saving || dirtyCount === 0}
                  data-testid="schedule-save">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Save schedule
          </Button>
        </div>
      </div>
    </div>
  );
}
